import { useAtom } from 'jotai';
import React, { useState, useMemo } from 'react';
import { Container, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { jrLessonsPagePng } from '../../assets/images';
import { YouTubeVideo } from '../../components/YouTubeVideo';
import { SITE_LINKS } from '../../constants';
import { PlatformTourYoutubeVideoID } from '../../enum';
import { userAtom } from '../../jotai-atoms/user.atom';
import { Background } from '../../stories/Background';
import { Button } from '../../stories/Button';
import { Header } from '../../stories/Header';
import { OnboardingNavbar } from '../../stories/OnboardingNavbar';

export const OnboardingVideoTutorialPage: React.FC = () => {
  const [user] = useAtom(userAtom);

  const [showModal, setShowModal] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const __ = useTranslation().t;

  const youtubeVideoId = useMemo(() => {
    if (user?.lang === 'pt-BR') {
      return PlatformTourYoutubeVideoID.BR;
    }

    return PlatformTourYoutubeVideoID.LATAM;
  }, [user?.lang]);

  const modalArgs = {
    show: showModal,
    setShow: setShowModal,
    youtubeVideoId,
    setEnded: setVideoEnded,
  };

  const NextButton = () => {
    if (!videoEnded) {
      return <></>;
    }
    return (
      <Button
        id="onboarding-page-1-next-button"
        icon="arrow-right"
        label={__('continue')}
        variant="primary"
        size="lg"
        href={SITE_LINKS.OnboardingPage(`recommendations`)}
      />
    );
  };

  const handleClickWatchVideoButton = () => {
    setShowModal(true);
    setVideoEnded(true);
    window.localStorage.setItem('oejr_onboarding_video_ended', 'true');
  };

  return (
    <>
      <Header type="logo-only" />
      <Background>
        <Container>
          <div className="oejr-page-onboarding oejr-page-onboarding-1">
            <OnboardingNavbar numberOfCompletedSteps={0} totalSteps={3} />
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-md-between">
              <div className="oejr-page-onboarding-arguments">
                <h1 className="oejr-onboarding-main-argument">{__('walkAroundThePlatform')}</h1>
                <p>{__('watchVideoTutorialToBecomeFamiliar')}</p>
                <Button
                  icon="play"
                  label={__('watchTutorial')}
                  onClick={handleClickWatchVideoButton}
                  variant="primary"
                  outline={videoEnded}
                  size="lg"
                />
              </div>
              <div className="oejr-page-onboarding-image-wrapper">
                <img
                  src={jrLessonsPagePng}
                  alt="Main Argument"
                  className="position-relative"
                  style={{ zIndex: 1 }}
                />
                <div className="oejr-page-onboarding-1-rectangle" />
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <NextButton />
            </div>
            <VideoModal {...modalArgs} />
          </div>
        </Container>
      </Background>
    </>
  );
};

interface VideoModalProps {
  show: boolean;
  setShow: (_: boolean) => void;
  setEnded: (_: boolean) => void;
  youtubeVideoId: string;
}

const VideoModal: React.FC<VideoModalProps> = ({ show, setShow, setEnded, youtubeVideoId }) => {
  const onEnd = () => {
    setShow(false);
    setEnded(true);
    window.localStorage.setItem('oejr_onboarding_video_ended', 'true');
  };
  return (
    <Modal show={show} onHide={() => setShow(false)} keyboard={false} id="onboarding-page-1-modal">
      <Modal.Body>
        <div>
          <div className="embed-container">
            <YouTubeVideo autoplayEnabled videoId={youtubeVideoId} onEnd={onEnd} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
